/**
 * CRITICAL SECURITY UTILITY
 * 
 * This file contains security filters for notifications to ensure
 * client users ONLY see notifications they are authorized to view.
 * 
 * IMPORTANT: Client users should NEVER see:
 * - Internal team comments
 * - Notifications for tickets they didn't create
 * - System notifications
 * - Any confidential information
 */

/**
 * Determines if a client user should see a specific notification
 * @param {Object} notification - The notification object
 * @param {Object} user - The current user object
 * @returns {boolean} - True if notification should be shown, false otherwise
 */
export const shouldClientUserSeeNotification = (notification, user) => {
  // If not a client user, show all notifications
  if (!user || user.role !== 'Client') {
    return true;
  }

  // CRITICAL SECURITY: Client users should ONLY see specific notifications
  // to prevent exposure of confidential information
  
  // Check if notification has metadata indicating it's from an internal comment
  if (notification.metadata) {
    try {
      const metadata = typeof notification.metadata === 'string' 
        ? JSON.parse(notification.metadata) 
        : notification.metadata;
      
      // Block internal comments/events
      if (metadata.is_internal === true || metadata.internal === true) {
        return false;
      }
    } catch (e) {
      console.error('Error parsing notification metadata:', e);
    }
  }

  // Check notification type - only allow specific event types for clients
  const allowedClientNotificationTypes = [
    'comment_added',        // New comment on their ticket (non-internal only)
    'status_changed',       // Status update on their ticket
  ];

  // Block if notification type is not in allowed list
  if (notification.type && !allowedClientNotificationTypes.includes(notification.type)) {
    return false;
  }

  // For ticket-related notifications, verify it's a ticket notification
  if (notification.entity_type === 'ticket' || notification.ticket_id) {
    // Client should only see ticket-category notifications
    // The backend already filters to ensure they only get their own tickets
    if (notification.category !== 'ticket') {
      return false;
    }
  }

  return true;
};

/**
 * Filter an array of notifications for client users
 * @param {Array} notifications - Array of notification objects
 * @param {Object} user - The current user object
 * @returns {Array} - Filtered array of notifications
 */
export const filterNotificationsForClient = (notifications, user) => {
  if (!user || user.role !== 'Client') {
    return notifications;
  }

  return notifications.filter(notification => 
    shouldClientUserSeeNotification(notification, user)
  );
};
